/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect } from 'react'
import { css } from '@emotion/react'
import { Modal, DatePicker, Input, Checkbox } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import req from '@sportninja/common/api/request'

const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

interface NewFlightModalProps {
  isOpen: boolean
  onClose: () => void
  scheduleId: string
  currentFlightId?: string | null
  nextFlightNumber: number
  onFlightCreated: (flightId: string) => void
}

const NewFlightModal: React.FC<NewFlightModalProps> = ({
  isOpen,
  onClose,
  scheduleId,
  currentFlightId,
  nextFlightNumber,
  onFlightCreated,
}) => {
  const [label, setLabel] = useState(`Flight ${nextFlightNumber}`)
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null)
  const [copyConfig, setCopyConfig] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [endDateOpen, setEndDateOpen] = useState(false)
  const endDateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (endDateTimerRef.current) {
        clearTimeout(endDateTimerRef.current)
      }
    }
  }, [])

  const handleCreate = async () => {
    setSaving(true)
    setErrorMessage(null)
    try {
      const body: Record<string, unknown> = {
        label: label.trim() || null,
        start_date: startDate ? startDate.format('YYYY-MM-DD') : null,
        end_date: endDate ? endDate.format('YYYY-MM-DD') : null,
      }

      let response
      if (copyConfig && currentFlightId) {
        response = await req(`/scheduler/flights/${currentFlightId}/clone`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
      } else {
        response = await req(`/scheduler/schedules/${scheduleId}/flights`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
      }

      const flightData = response?.data?.data ?? response?.data
      const newFlightId = flightData?.id
      if (newFlightId) {
        onFlightCreated(newFlightId)
      }
      onClose()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setErrorMessage(
        err?.message || 'An error occurred while creating the flight.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={440}
      destroyOnClose
      maskClosable
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_100};
        `}
      >
        <h2
          css={css`
            color: ${colors.WHITE};
            margin: 0;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
            font-size: 20px;
            font-weight: 600;
          `}
        >
          Add New Flight
        </h2>
        <button
          type='button'
          css={css`
            background: transparent;
            border: none;
            color: ${colors.WHITE};
            cursor: pointer;
            padding: 0;
            display: flex;
            align-items: center;
            font-size: 18px;
            &:hover {
              opacity: 0.7;
            }
          `}
          onClick={onClose}
          aria-label='Close modal'
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Body */}
      <div
        css={css`
          padding: 20px 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {/* Flight Name */}
        <div>
          <label
            css={css`
              display: block;
              font-family: 'Inter', sans-serif;
              font-size: 13px;
              font-weight: 500;
              color: ${colors.NEUTRAL_50};
              margin-bottom: 6px;
            `}
          >
            Flight Name
          </label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder='e.g. Spring Season'
            maxLength={100}
            css={css`
              background: ${colors.SECONDARY_400} !important;
              border-color: ${colors.SECONDARY_100} !important;
              color: ${colors.WHITE} !important;
              &::placeholder {
                color: ${colors.NEUTRAL_80} !important;
              }
            `}
          />
        </div>

        {/* Date Range */}
        <div
          css={css`
            display: flex;
            gap: 12px;
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            <label
              css={css`
                display: block;
                font-family: 'Inter', sans-serif;
                font-size: 13px;
                font-weight: 500;
                color: ${colors.NEUTRAL_50};
                margin-bottom: 6px;
              `}
            >
              Start Date
            </label>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                setStartDate(date)
                if (date) {
                  if (endDateTimerRef.current) {
                    clearTimeout(endDateTimerRef.current)
                  }
                  endDateTimerRef.current = setTimeout(
                    () => setEndDateOpen(true),
                    150
                  )
                }
              }}
              css={css`
                width: 100%;
                background: ${colors.SECONDARY_400} !important;
                border-color: ${colors.SECONDARY_100} !important;
                .ant-picker-input > input {
                  color: ${colors.WHITE} !important;
                }
              `}
            />
          </div>
          <div
            css={css`
              flex: 1;
            `}
          >
            <label
              css={css`
                display: block;
                font-family: 'Inter', sans-serif;
                font-size: 13px;
                font-weight: 500;
                color: ${colors.NEUTRAL_50};
                margin-bottom: 6px;
              `}
            >
              End Date
            </label>
            <DatePicker
              value={endDate}
              onChange={(date) => {
                setEndDate(date)
                setEndDateOpen(false)
              }}
              open={endDateOpen}
              onOpenChange={(open) => setEndDateOpen(open)}
              disabledDate={(current) =>
                startDate ? current.isBefore(startDate, 'day') : false
              }
              defaultPickerValue={startDate || undefined}
              css={css`
                width: 100%;
                background: ${colors.SECONDARY_400} !important;
                border-color: ${colors.SECONDARY_100} !important;
                .ant-picker-input > input {
                  color: ${colors.WHITE} !important;
                }
              `}
            />
          </div>
        </div>

        {/* Copy config checkbox */}
        {currentFlightId && (
          <Checkbox
            checked={copyConfig}
            onChange={(e) => setCopyConfig(e.target.checked)}
            css={css`
              .ant-checkbox-inner {
                background: ${colors.SECONDARY_400} !important;
                border-color: ${colors.SECONDARY_100} !important;
              }
              .ant-checkbox-checked .ant-checkbox-inner {
                background: ${colors.PRIMARY} !important;
                border-color: ${colors.PRIMARY} !important;
              }
              span {
                color: ${colors.NEUTRAL_50};
                font-family: 'Inter', sans-serif;
                font-size: 13px;
              }
            `}
          >
            Copy configuration from current flight
          </Checkbox>
        )}

        {errorMessage && (
          <div
            css={css`
              background: ${colors.ERROR_50};
              border: 1px solid ${colors.ERROR_200};
              border-radius: 6px;
              padding: 10px 12px;
              color: ${colors.ERROR_300};
              font-family: 'Inter', sans-serif;
              font-size: 13px;
              line-height: 1.4;
            `}
          >
            {errorMessage}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid ${colors.SECONDARY_100};
        `}
      >
        <button
          type='button'
          onClick={onClose}
          disabled={saving}
          css={css`
            padding: 8px 16px;
            border-radius: 8px;
            border: 1px solid ${colors.SECONDARY_100};
            background: transparent;
            color: ${colors.NEUTRAL_50};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            text-transform: uppercase;
            &:hover {
              color: ${colors.WHITE};
            }
          `}
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={handleCreate}
          disabled={saving}
          css={css`
            padding: 8px 16px;
            border-radius: 8px;
            border: none;
            background: ${colors.PRIMARY};
            color: ${colors.PRIMARY_TEXT};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            text-transform: uppercase;
            opacity: ${saving ? 0.6 : 1};
            &:hover {
              background: ${colors.PRIMARY_DARK};
            }
          `}
        >
          {saving ? 'Creating...' : 'Create'}
        </button>
      </div>
    </Modal>
  )
}

export default NewFlightModal
